<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('customer.detailTitle') }}</span>
        </div>
      </template>

      <!-- Avatar Section -->
      <div class="avatar-section">
        <div v-if="customerData.avatar" @click="previewAvatar(customerData.avatar)">
          <img :src="customerData.avatar" alt="Avatar" class="detail-avatar" />
        </div>
        <div v-else class="detail-avatar-placeholder">
          <el-icon :size="64"><Avatar /></el-icon>
        </div>
      </div>

      <el-descriptions :column="1" border>
        <el-descriptions-item :label="$t('customer.name')">
          {{ customerData.name }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('customer.phone')">
          {{ customerData.phone }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('customer.gender')">
          {{ customerData.gender ? $t('customer.male') : $t('customer.female') }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('customer.age')">
          {{ customerData.age || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('customer.level')">
          {{ $t(getLevelText(customerData.level)) }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('customer.status')">
          {{ $t(getStatusText(customerData.status)) }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('customer.remark')">
          {{ customerData.remark || '-' }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('common.createdAt')">
          {{ customerData.createdAt }}
        </el-descriptions-item>
      </el-descriptions>

      <div class="mt-4">
        <el-button @click="goBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" @click="goToEdit">
          {{ $t('common.edit') }}
        </el-button>
      </div>
    </el-card>

    <!-- Image Preview Dialog -->
    <el-dialog v-model="showImagePreview" :title="$t('customer.avatarPreview')" width="500px" center>
      <div style="text-align: center;">
        <img :src="previewImageUrl" alt="Avatar Preview" style="max-width: 100%; max-height: 80vh; border-radius: 8px;" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'

const { t } = useI18n()
import { getCustomerDetail } from '@/api/customer'
import { Avatar } from '@element-plus/icons-vue'

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const customerData = ref({})
const showImagePreview = ref(false)
const previewImageUrl = ref('')

const getLevelText = (level) => {
  const levelMap = {
    0: 'customer.beginner',
    1: 'customer.junior',
    2: 'customer.senior'
  }
  return levelMap[level] ? levelMap[level] : '-'
}

const getStatusText = (status) => {
  return status === 1 ? 'customer.active' : 'customer.inactive'
}

const loadCustomer = async () => {
  const id = route.params.id
  if (!id) return
  
  loading.value = true
  try {
    const response = await getCustomerDetail(id)
    customerData.value = response.data
  } catch (error) {
    ElMessage.error('Failed to load customer')
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  router.push('/customer')
}

const goToEdit = () => {
  router.push(`/customer/edit/${route.params.id}`)
}

const previewAvatar = (avatarUrl) => {
  if (!avatarUrl) return
  previewImageUrl.value = avatarUrl
  showImagePreview.value = true
}

onMounted(() => {
  loadCustomer()
})
</script>

<style scoped>
.avatar-section {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.detail-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.detail-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(64, 158, 255, 0.3);
}

.detail-avatar-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #f0f2f5;
  border: 3px solid #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
}
</style>
